import { useEffect, useRef, useState } from 'react';
import { TURNSTILE_SITE_KEY } from '../constants';

const SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileApi = {
  render: (
    elt: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
    },
  ) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/**
 * Cloudflare Turnstile captcha. Render `widget` in the form, send `token` as
 * `captcha_token` (checked by `requireTurnstile` in fpaccount), and call
 * `reset` after each submit since tokens are single use.
 */
export const useTurnstile = () => {
  const [token, setToken] = useState<string | null>(null);
  // bumped to remount the widget, which fetches a fresh token
  const [widgetKey, setWidgetKey] = useState(0);

  const widget = <TurnstileWidget key={widgetKey} onToken={setToken} />;
  const reset = () => {
    setToken(null);
    setWidgetKey((k) => k + 1);
  };

  return { token, widget, reset };
};

// ## Helpers

const TurnstileWidget = ({
  onToken,
}: {
  onToken: (token: string | null) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let widgetId: string | undefined;

    _loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !ref.current) {
          return;
        }
        widgetId = turnstile.render(ref.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(null),
          'error-callback': () => onToken(null),
        });
      })
      .catch((error) => console.error('Turnstile failed to load', error));

    return () => {
      cancelled = true;
      if (widgetId) {
        window.turnstile?.remove(widgetId);
      }
    };
  }, [onToken]);

  return <div ref={ref} />;
};

let _turnstilePromise: Promise<TurnstileApi> | null = null;

const _loadTurnstile = () => {
  if (!_turnstilePromise) {
    _turnstilePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () =>
        window.turnstile
          ? resolve(window.turnstile)
          : reject(new Error('window.turnstile missing'));
      script.onerror = () => {
        // allow a retry on the next mount, e.g. after a network blip
        _turnstilePromise = null;
        script.remove();
        reject(new Error(`Failed to load ${SCRIPT_URL}`));
      };
      document.head.appendChild(script);
    });
  }
  return _turnstilePromise;
};
